// The marks key of the squad chip (R1571; mockup round 075, card 17;
// ui-map-interface.md §8).
//
// The key teaches the three degrees of R185 — your own, your squad's and
// the rest of the faction's — with one hull and one hold each, and the
// squad chip of the top bar wears the squad hull as its icon. **The key
// must draw what the map draws** (R1558 open item 3): every mark here comes
// off the reader's own look, through the same tables the fleet layer and
// the site layer read. The AUTO squad value is `trailing-dot` (R1279) and
// not the hollow hull of R185, so a fixed picture would teach the wrong
// mark.
//
// A hull is a PLAN first and a drawing second. `keyHullPlan` answers what
// the fleet layer would put down for one degree — the silhouette, the
// hollow master or the filled one, the ink, the scale, the bloom, the
// cells beside the hull and the slot numeral — and `drawKeyHull` lays the
// cells and the hull down on any `MarkGraphics`. The bloom is an ellipse
// and the numeral is text, which a `MarkGraphics` cannot state, so the 2D
// painter of `ui/squadmarks.ts` draws those two from the plan.
//
// This is a leaf: it reads the generated pack and the mark tables, and it
// imports no engine.

import {
  FLEET_BLOOM_HALF_LEN, FLEET_BLOOM_HALF_W, FLEET_GROUP_BADGE_H, FLEET_HOLLOW_STROKE_PX,
  FLEET_OWN_HULL_SCALE, MARK_INKS,
} from '../gen/presentation';
import { ShipClass } from '../gen/proto/aquaphobia';
import {
  OWN_FLEET_MARKS, SQUAD_FLEET_MARKS, type FleetMarkCell, type SlotNumeral,
} from './fleet-marks';
import { drawHoldLight, drawHoldMark, type HoldEra, type HoldSymbolId } from './hold-marks';
import { hullPathOf, shipStyleOf } from './hull-styles';
import {
  BoxProbe, lift, type HullPath, type MarkBox, type MarkFill, type MarkGraphics, type MarkStroke,
} from './marks';
import type { SceneLook } from './scene-look';

/** The three degrees of R185 the key draws, in its row order. */
export type KeyRank = 'own' | 'mate' | 'faction';

/** The hull the key draws: the Corsair of card 17. */
export const KEY_CLASS = ShipClass.SHIP_CLASS_CORSAIR;

/** The white of an own hull and of a white part: a dark core in the light
 *  identity (R3296). */
const WHITE = MARK_INKS.white;

/** One cell beside the hull, with the ink and the alpha its quad carries. */
export interface KeyCell {
  cell: FleetMarkCell;
  ink: number;
  alpha: number;
}

/** What the fleet layer would put down for one degree of one look. */
export interface KeyHullPlan {
  path: HullPath;
  /** The stroked master (`hollow-hull`) and not the filled one. */
  hollow: boolean;
  /** The ink of the hull. */
  ink: number;
  /** The own white core is a little larger (`FLEET_OWN_HULL_SCALE`). */
  scale: number;
  /** The own white core stands on the axial bloom. */
  bloom: boolean;
  /** The marks beside the hull, drawn UNDER it, as `packFleetMarks` does. */
  cells: KeyCell[];
  /** The squad slot numeral (`slot-numeral`), or null. */
  slot: (SlotNumeral & { ink: number }) | null;
}

/**
 * The hull of one degree under one look, by the rules of `packHulls` and
 * `packFleetMarks` (fast/layers/fleets.ts). `ink` is the faction ink.
 */
export function keyHullPlan(look: SceneLook, rank: KeyRank, ink: number): KeyHullPlan {
  const path = hullPathOf(shipStyleOf(look, KEY_CLASS), KEY_CLASS);
  const plan: KeyHullPlan = {
    path, hollow: false, ink, scale: 1, bloom: false, cells: [], slot: null,
  };
  if (rank === 'faction') return plan;
  const style = rank === 'own' ? OWN_FLEET_MARKS[look.ownFleet] : SQUAD_FLEET_MARKS[look.squadFleet];
  if (rank === 'own' && look.ownFleet === 'white-core') {
    plan.ink = WHITE;
    plan.scale = FLEET_OWN_HULL_SCALE;
    plan.bloom = true;
  }
  if (rank === 'mate') {
    plan.hollow = look.squadFleet === 'hollow-hull';
    if (style.lift !== undefined) plan.ink = lift(ink, style.lift);
  }
  plan.cells = style.cells.map((cell) => ({
    cell, ink: cell.tint === 'white' ? WHITE : ink, alpha: cell.alpha,
  }));
  if (style.slot) plan.slot = { ...style.slot, ink };
  return plan;
}

/**
 * A `MarkGraphics` that paints every fill and every stroke in one ink, at
 * a share of the alpha the description asked for. A mark cell paints
 * WHITE and the quad of the map carries the ink and the alpha; here the
 * paint carries both.
 */
export class InkedMarks implements MarkGraphics {
  constructor(
    private readonly g: MarkGraphics,
    private readonly ink: number,
    private readonly alpha: number,
  ) {}

  moveTo(x: number, y: number): this { this.g.moveTo(x, y); return this; }
  lineTo(x: number, y: number): this { this.g.lineTo(x, y); return this; }
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw = false): this {
    this.g.arc(x, y, r, a0, a1, ccw);
    return this;
  }
  closePath(): this { this.g.closePath(); return this; }
  poly(points: number[], close = true): this { this.g.poly(points, close); return this; }
  circle(x: number, y: number, r: number): this { this.g.circle(x, y, r); return this; }
  fill(style: MarkFill): this {
    this.g.fill({ color: this.ink, alpha: (style.alpha ?? 1) * this.alpha });
    return this;
  }
  stroke(style: MarkStroke): this {
    this.g.stroke({ ...style, color: this.ink, alpha: (style.alpha ?? 1) * this.alpha });
    return this;
  }
}

/**
 * Lays down the cells and the hull of a plan at the origin, the hull
 * nose to +x. The caller places the context and applies `plan.scale` to
 * the hull alone; the bloom and the slot numeral are the caller's (the
 * head of this file says why).
 */
export function drawKeyCells(g: MarkGraphics, plan: KeyHullPlan): void {
  for (const c of plan.cells) c.cell.draw(new InkedMarks(g, c.ink, c.alpha));
}

/** Lays down the silhouette of a plan, filled or stroked, in its ink. */
export function drawKeyHull(g: MarkGraphics, plan: KeyHullPlan): void {
  plan.path(g);
  if (plan.hollow) g.stroke({ width: FLEET_HOLLOW_STROKE_PX, color: plan.ink, join: 'round' });
  else g.fill({ color: plan.ink });
}

/**
 * The hold of one degree: the rank's own mark of the look under the
 * symbol, and the light of an own hold, the way the site layer draws them
 * (`drawHoldMark`, §6.2). A faction hold wears no mark.
 */
export function drawKeyHold(
  g: MarkGraphics, look: SceneLook, rank: KeyRank, symbol: HoldSymbolId, era: HoldEra, ink: number,
): void {
  if (rank === 'own') {
    drawHoldMark(g, symbol, era, ink, 'own', look.ownHold);
    drawHoldLight(g, symbol, era);
  } else if (rank === 'mate') {
    drawHoldMark(g, symbol, era, ink, 'mate', look.squadHold);
  } else {
    drawHoldMark(g, symbol, era, ink, 'faction');
  }
}

/**
 * The other hull value of R185 the foot of the key draws (card 17): the
 * hollow hull for every reader whose squad value is not the hollow hull,
 * and the AUTO trailing dot for the reader whose value is.
 */
export function otherSquadValue(look: SceneLook): SceneLook['squadFleet'] {
  return look.squadFleet === 'hollow-hull' ? 'trailing-dot' : 'hollow-hull';
}

/** How tall the slot numeral stands, in map px: the group badge's height,
 *  which the fleet layer sets the numeral at (`packSlotNumeral`). */
const KEY_SLOT_H = FLEET_GROUP_BADGE_H;

/** The box of a hull plan: the cells, the hull at its scale, the bloom and
 *  the numeral, in map px about the hull's origin. */
export function keyHullBox(plan: KeyHullPlan): MarkBox {
  const cells = new BoxProbe();
  drawKeyCells(cells, plan);
  const hull = new BoxProbe();
  drawKeyHull(hull, plan);
  const boxes = [cells.result(), scaled(hull.result(), plan.scale)];
  if (plan.bloom) {
    boxes.push({ x0: -FLEET_BLOOM_HALF_LEN, y0: -FLEET_BLOOM_HALF_W, x1: FLEET_BLOOM_HALF_LEN, y1: FLEET_BLOOM_HALF_W });
  }
  if (plan.slot) {
    const h = KEY_SLOT_H;
    boxes.push({ x0: plan.slot.dx, y0: plan.slot.dy - h / 2, x1: plan.slot.dx + h, y1: plan.slot.dy + h / 2 });
  }
  return union(boxes);
}

function scaled(b: MarkBox | null, k: number): MarkBox | null {
  return b && { x0: b.x0 * k, y0: b.y0 * k, x1: b.x1 * k, y1: b.y1 * k };
}

function union(list: readonly (MarkBox | null)[]): MarkBox {
  const out: MarkBox = { x0: 0, y0: 0, x1: 0, y1: 0 };
  for (const b of list) {
    if (!b) continue;
    out.x0 = Math.min(out.x0, b.x0); out.y0 = Math.min(out.y0, b.y0);
    out.x1 = Math.max(out.x1, b.x1); out.y1 = Math.max(out.y1, b.y1);
  }
  return out;
}
